import { Box, Flex, Icon, Text } from '@chakra-ui/react'
import { ChevronDown } from 'lucide-react'
import { Key, useCallback, useId, useMemo, useState } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover'
import {
  SiteNavigationExpandableTabSizes,
  useSiteNavigationExpandableTabStyle,
} from '~/pages/Home/SiteNavigation/componentStyles/SiteNavigationExpandableTab.style'

export interface ExpandableTabChild {
  id: string
  label: string
  path: string
}

interface SiteNavigationExpandableTabProps {
  id?: Key
  title: string
  icon?: SVGComponent
  isCollapsed?: boolean
  isExpanded?: boolean
  isAnyChildActive?: boolean
  children: ExpandableTabChild[]
  activeChildPath?: string
  onToggleExpand?: () => void
  onChildClick?: (path: string) => void
}

export const SiteNavigationExpandableTab = ({
  title,
  icon,
  isCollapsed,
  isExpanded,
  isAnyChildActive,
  children,
  activeChildPath,
  onToggleExpand,
  onChildClick,
}: SiteNavigationExpandableTabProps) => {
  const styles = useSiteNavigationExpandableTabStyle({
    size: isCollapsed
      ? SiteNavigationExpandableTabSizes.Collapsed
      : SiteNavigationExpandableTabSizes.Normal,
  })

  const [popoverOpen, setPopoverOpen] = useState(false)
  const childrenContainerId = useId()
  const anyChildActiveProps = {
    'data-selected': isAnyChildActive ? '' : undefined,
  }

  const handleChildClickAndClose = useCallback(
    (path: string) => {
      setPopoverOpen(false)
      onChildClick?.(path)
    },
    [onChildClick],
  )

  const renderChildItems = useCallback(
    (items: ExpandableTabChild[], options?: { inPopover?: boolean }) =>
      items.map((child) => {
        const isActive = activeChildPath === child.path
        return (
          <Flex
            asChild
            key={child.id}
            css={styles.childItem}
            pl={options?.inPopover ? '0.5rem' : undefined}
          >
            <button
              type="button"
              aria-current={isActive ? 'page' : undefined}
              onClick={() =>
                options?.inPopover
                  ? handleChildClickAndClose(child.path)
                  : onChildClick?.(child.path)
              }
            >
              <Box css={styles.childDot} data-active={isActive} />
              <Text css={styles.childText} data-active={isActive} lineClamp={1}>
                {child.label}
              </Text>
            </button>
          </Flex>
        )
      }),
    [
      activeChildPath,
      styles.childItem,
      styles.childDot,
      styles.childText,
      handleChildClickAndClose,
      onChildClick,
    ],
  )

  const content = useMemo(() => {
    if (isCollapsed) {
      return (
        <Flex css={styles.container}>
          <Popover
            open={popoverOpen}
            onOpenChange={({ open }) => setPopoverOpen(open)}
            positioning={{ placement: 'right-start', gutter: 8 }}
          >
            <PopoverTrigger asChild>
              <Flex asChild css={styles.headerSelectionContainer}>
                <button
                  type="button"
                  aria-label={title}
                  {...anyChildActiveProps}
                >
                  {icon && (
                    <Icon
                      as={icon}
                      {...anyChildActiveProps}
                      css={styles.headerIcon}
                    />
                  )}
                </button>
              </Flex>
            </PopoverTrigger>
            <PopoverContent
              width="auto"
              minWidth="10rem"
              p="0.375rem"
              borderRadius="0.5rem"
              boxShadow="md"
            >
              <Text
                css={styles.headerTitleText}
                {...anyChildActiveProps}
                px="0.5rem"
                py="0.375rem"
                fontWeight="600"
              >
                {title}
              </Text>
              <Flex flexDirection="column">
                {renderChildItems(children, { inPopover: true })}
              </Flex>
            </PopoverContent>
          </Popover>
        </Flex>
      )
    }

    return (
      <Flex css={styles.container}>
        <Flex css={styles.headerContainer}>
          <Flex asChild css={styles.headerSelectionContainer}>
            <button
              type="button"
              aria-expanded={Boolean(isExpanded)}
              aria-controls={childrenContainerId}
              {...anyChildActiveProps}
              onClick={onToggleExpand}
            >
              <Flex css={styles.headerIconWrapper}>
                {icon && (
                  <Icon
                    as={icon}
                    {...anyChildActiveProps}
                    css={styles.headerIcon}
                  />
                )}
                <Text
                  css={styles.headerTitleText}
                  lineClamp={1}
                  {...anyChildActiveProps}
                >
                  {title}
                </Text>
              </Flex>
              <Icon
                as={ChevronDown}
                css={styles.chevronIcon}
                transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
              />
            </button>
          </Flex>
        </Flex>

        <Flex
          id={childrenContainerId}
          hidden={!isExpanded}
          css={styles.childrenContainer}
        >
          {isExpanded && renderChildItems(children)}
        </Flex>
      </Flex>
    )
  }, [
    isCollapsed,
    isExpanded,
    isAnyChildActive,
    activeChildPath,
    popoverOpen,
    title,
    icon,
    children,
    styles,
    renderChildItems,
    onToggleExpand,
    childrenContainerId,
  ])

  return <>{content}</>
}
